import { useState, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { UnifiedCard } from "@/components/cards/UnifiedCard";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Plus, Globe, Loader2, Users, Upload, FileText, X,
  Hammer, PenLine, Wand2, LayoutTemplate, LayoutGrid, List,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ShareWorldDialog } from "@/components/world/ShareWorldDialog";
import { WorldCardActions } from "@/components/cards/WorldCardActions";
import { CreditBadge } from "@/components/credits/CreditBadge";
import { TemplateSelector } from "@/components/world/TemplateSelector";
import { useLocale } from "@/hooks/useLocale";
import { fmtDate } from "@/lib/locale-format";
import { getLocalizedWorldName, getLocalizedWorldDescription } from "@/lib/world-i18n";
import { toast } from "sonner";
import type { Translations } from "@/lib/i18n/types";
import { WorldStatsRow } from "./WorldStatsRow";
import { ContextFileDropZone } from "./ContextFileDropZone";

type ViewMode = "grid" | "list";

const UNCATEGORIZED_CATEGORY = "uncategorized";

function getStoredViewMode(): ViewMode {
  try {
    const v = localStorage.getItem("worlds_view_mode");
    return v === "list" ? "list" : "grid";
  } catch { return "grid"; }
}

function getStoredCategory(): string {
  try { return localStorage.getItem("worlds_category") || "all"; } catch { return "all"; }
}

function normalizeTemplateCategory(templateKey?: string | null): string {
  if (!templateKey || typeof templateKey !== "string") return UNCATEGORIZED_CATEGORY;
  const normalized = templateKey.trim().toLowerCase().replace(/_/g, " ");
  return normalized.length > 0 && normalized.length <= 30 ? normalized : UNCATEGORIZED_CATEGORY;
}

function formatCategoryLabel(categoryKey: string): string {
  if (categoryKey === "all") return "All";
  if (categoryKey === UNCATEGORIZED_CATEGORY) return "Uncategorized";
  return categoryKey.replace(/\b\w/g, (char) => char.toUpperCase());
}

interface BuildTabProps {
  t: Translations;
  worlds: any[];
  sharedWorlds: any[];
  showNewWorld: boolean;
  setShowNewWorld: (v: boolean) => void;
  creationMode: "manual" | "prompt" | "template";
  setCreationMode: (v: "manual" | "prompt" | "template") => void;
  newWorldName: string;
  setNewWorldName: (v: string) => void;
  newWorldDesc: string;
  setNewWorldDesc: (v: string) => void;
  newWorldPrompt: string;
  setNewWorldPrompt: (v: string) => void;
  contextFiles: { name: string; content: string }[];
  setContextFiles: (v: { name: string; content: string }[]) => void;
  creating: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCreateWorld: (override?: { name: string; desc: string; gameKey: string }) => void;
  handleDeleteWorld: (id: string) => void;
  handleCloneWorld: (id: string, name: string) => Promise<void>;
  handlePublishWorld: (id: string, isPublic: boolean) => void;
  navigate: (path: string) => void;
  setShowNewGame: (v: boolean) => void;
  setSelectedWorldId: (v: string) => void;
  worldStats: Record<string, any>;
  worldCredits: Record<string, number>;
  cloningWorldId?: string | null;
}

export function BuildTab({
  t, worlds, sharedWorlds, showNewWorld, setShowNewWorld,
  creationMode, setCreationMode,
  newWorldName, setNewWorldName, newWorldDesc, setNewWorldDesc,
  newWorldPrompt, setNewWorldPrompt,
  contextFiles, setContextFiles, creating, fileInputRef,
  handleFileUpload, handleCreateWorld, handleDeleteWorld, handleCloneWorld, handlePublishWorld,
  navigate, setShowNewGame, setSelectedWorldId, worldStats, worldCredits,
  cloningWorldId,
}: BuildTabProps) {
  const { locale } = useLocale();
  const isPromptMode = creationMode === "prompt";
  const isTemplateMode = creationMode === "template";
  const canCreate = isPromptMode ? newWorldPrompt.trim().length > 0 : isTemplateMode ? false : true;

  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const [category, setCategory] = useState<string>(getStoredCategory);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem("worlds_view_mode", mode); } catch {}
  };

  const handleCategoryChange = (cat: string) => {
    setCategory(cat);
    try { localStorage.setItem("worlds_category", cat); } catch {}
  };

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const world of worlds) {
      cats.add(normalizeTemplateCategory(world.config?.template_key));
    }
    return ["all", ...Array.from(cats).sort()];
  }, [worlds]);

  useEffect(() => {
    if (!categories.includes(category)) {
      handleCategoryChange("all");
    }
  }, [categories, category]);

  const filteredWorlds = useMemo(() => {
    if (category === "all") return worlds;
    return worlds.filter((world: any) => normalizeTemplateCategory(world.config?.template_key) === category);
  }, [worlds, category]);

  const renderWorldCard = (world: any, role: "owner" | "edit" | "play" | "view" | "public" = "owner", extraFooter?: React.ReactNode) => {
    const isCloning = cloningWorldId === world.id;
    return (
      <UnifiedCard
        key={world.id}
        id={world.id}
        cardType={role === "owner" ? "world" : "shared"}
        onClick={() => navigate(`/world/${world.id}`)}
        bannerUrl={world.banner_url}
        icon={world.icon}
        title={getLocalizedWorldName(world, locale)}
        description={getLocalizedWorldDescription(world, locale)}
        confidential={!!world.is_confidential}
        badgesLeft={world.is_confidential ? <Badge className="bg-red-600 text-white border-transparent text-[10px]">Confidential</Badge> : undefined}
        extraContent={<WorldStatsRow stats={worldStats[world.id]} />}
        actions={
          isCloning ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Duplicating...
            </div>
          ) : (
            <WorldCardActions
              worldId={world.id}
              worldName={world.name}
              role={role}
              allowCloning={world.allow_cloning}
              isPublic={world.is_public}
              onView={() => navigate(`/world/${world.id}`)}
              onEdit={role === "owner" || role === "edit" ? () => navigate(`/world/${world.id}`) : undefined}
              onPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
              onPublishToggle={role === "owner" ? () => handlePublishWorld(world.id, !!world.is_public) : undefined}
              onTestPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
              onDuplicate={() => handleCloneWorld(world.id, world.name)}
              onDelete={role === "owner" ? () => handleDeleteWorld(world.id) : undefined}
            />
          )
        }
        footer={
          extraFooter || (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {t.idx_updated} {fmtDate(new Date(world.updated_at), locale)}
              </span>
              {(worldCredits[world.id] || 0) > 0 && <CreditBadge amount={worldCredits[world.id]} />}
            </div>
          )
        }
      />
    );
  };

  return (
    <div className="space-y-10">
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            {t.idx_your_worlds}
          </h2>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center border border-border rounded-md overflow-hidden">
              <button
                onClick={() => handleViewChange("grid")}
                className={cn(
                  "p-1.5 transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleViewChange("list")}
                className={cn(
                  "p-1.5 transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <Dialog open={showNewWorld} onOpenChange={setShowNewWorld}>
              <DialogTrigger asChild>
                <Button className="gap-2"><Plus className="w-4 h-4" />{t.idx_new_world}</Button>
              </DialogTrigger>
              <DialogContent className={cn("max-h-[90vh] overflow-y-auto", isTemplateMode ? "max-w-2xl" : "")}>
                <DialogHeader><DialogTitle>{t.idx_create_new_world}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setCreationMode("manual")}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        creationMode === "manual"
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:border-primary/40"
                      }`}
                    >
                      <PenLine className="w-4 h-4 shrink-0" />
                      <div>
                        <div className="text-sm font-medium">{t.idx_manual}</div>
                        <div className="text-[10px] text-muted-foreground">{t.idx_manual_desc}</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setCreationMode("prompt")}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        creationMode === "prompt"
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:border-primary/40"
                      }`}
                    >
                      <Wand2 className="w-4 h-4 shrink-0" />
                      <div>
                        <div className="text-sm font-medium">{t.idx_ai_prompt}</div>
                        <div className="text-[10px] text-muted-foreground">{t.idx_ai_prompt_desc}</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setCreationMode("template")}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        creationMode === "template"
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:border-primary/40"
                      }`}
                    >
                      <LayoutTemplate className="w-4 h-4 shrink-0" />
                      <div>
                        <div className="text-sm font-medium">Template</div>
                        <div className="text-[10px] text-muted-foreground">Start from a preset</div>
                      </div>
                    </button>
                  </div>

                  {isTemplateMode ? (
                    <TemplateSelector
                      onSelect={(gameKey: string, title: string, description: string) =>
                        handleCreateWorld({ name: title, desc: description, gameKey })
                      }
                    />
                  ) : (
                    <>
                      {isPromptMode ? (
                        <div className="space-y-3">
                          <Textarea
                            placeholder={t.idx_world_prompt_placeholder}
                            value={newWorldPrompt}
                            onChange={(e) => setNewWorldPrompt(e.target.value)}
                            rows={4}
                            className="resize-none"
                          />
                          <div className="space-y-2">
                            <ContextFileDropZone
                              onFilesAdded={(files) => setContextFiles([...contextFiles, ...files])}
                              t={t}
                            />
                            {contextFiles.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {contextFiles.map((f, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs gap-1 pr-1">
                                    <FileText className="w-3 h-3" />
                                    {f.name}
                                    <button onClick={() => setContextFiles(contextFiles.filter((_, j) => j !== i))} className="ml-0.5 hover:text-destructive">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <Input placeholder={t.idx_world_name_placeholder} value={newWorldName} onChange={(e) => setNewWorldName(e.target.value)} />
                          <Textarea placeholder={t.idx_world_desc_placeholder} value={newWorldDesc} onChange={(e) => setNewWorldDesc(e.target.value)} rows={3} className="resize-none" />
                        </>
                      )}

                      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-muted-foreground space-y-1">
                        <p className="font-medium text-amber-600 dark:text-amber-400 text-xs">{t.idx_terms_title}</p>
                        <p>{t.idx_terms_content_1}</p>
                        <p>{t.idx_terms_content_2}</p>
                      </div>

                      <Button onClick={() => handleCreateWorld()} disabled={creating || !canCreate} className="w-full">
                        {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {isPromptMode ? t.idx_create_with_ai : t.idx_create_and_build}
                      </Button>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Category filter chips */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium transition-colors border",
                  category === cat
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                )}
              >
                {formatCategoryLabel(cat)}
              </button>
            ))}
          </div>
        )}

        {filteredWorlds.length === 0 && worlds.length === 0 ? (
          <Card className="p-12 text-center space-y-4">
            <Globe className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <p className="text-muted-foreground">{t.idx_no_worlds_hint}</p>
          </Card>
        ) : filteredWorlds.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground text-sm">No worlds match this filter</p>
          </Card>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredWorlds.map((world: any) => renderWorldCard(world))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredWorlds.map((world: any) => (
              <div
                key={world.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:border-primary/30 transition-colors cursor-pointer group"
                onClick={() => navigate(`/world/${world.id}`)}
              >
                {/* Icon or mini banner */}
                <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                  {world.banner_url ? (
                    <img src={world.banner_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  ) : world.icon ? (
                    <span className="text-lg">{world.icon}</span>
                  ) : (
                    <Globe className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium truncate">{getLocalizedWorldName(world, locale)}</h3>
                    {world.is_public && <Badge variant="outline" className="text-[9px] px-1 py-0">Public</Badge>}
                    {world.is_confidential && <Badge className="bg-red-600 text-white border-transparent text-[9px] px-1 py-0">Confidential</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{getLocalizedWorldDescription(world, locale) || "No description"}</p>
                </div>
                {/* Stats */}
                <div className="hidden sm:flex items-center gap-3 shrink-0">
                  <WorldStatsRow stats={worldStats[world.id]} />
                  {(worldCredits[world.id] || 0) > 0 && <CreditBadge amount={worldCredits[world.id]} />}
                </div>
                {/* Updated date */}
                <span className="text-[10px] text-muted-foreground shrink-0 hidden md:block">
                  {fmtDate(new Date(world.updated_at), locale)}
                </span>
                {/* Actions */}
                <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                  {cloningWorldId === world.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : (
                    <WorldCardActions
                      worldId={world.id}
                      worldName={world.name}
                      role="owner"
                      allowCloning={world.allow_cloning}
                      isPublic={world.is_public}
                      onView={() => navigate(`/world/${world.id}`)}
                      onEdit={() => navigate(`/world/${world.id}`)}
                      onPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
                      onPublishToggle={() => handlePublishWorld(world.id, !!world.is_public)}
                      onTestPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
                      onDuplicate={() => handleCloneWorld(world.id, world.name)}
                      onDelete={() => handleDeleteWorld(world.id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {sharedWorlds.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-secondary" />
            {t.idx_shared_with_you}
          </h2>
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedWorlds.map((world: any) => renderWorldCard(
                world,
                world.permission,
                <p className="text-xs text-muted-foreground">by {world.owner_name}</p>,
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {sharedWorlds.map((world: any) => (
                <div
                  key={world.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:border-secondary/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/world/${world.id}`)}
                >
                  <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                    {world.banner_url ? (
                      <img src={world.banner_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    ) : world.icon ? (
                      <span className="text-lg">{world.icon}</span>
                    ) : (
                      <Globe className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium truncate">{getLocalizedWorldName(world, locale)}</h3>
                      <Badge variant={world.permission === "edit" ? "secondary" : "outline"} className="text-[9px] px-1 py-0">{world.permission}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">by {world.owner_name}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 shrink-0">
                    <WorldStatsRow stats={worldStats[world.id]} />
                  </div>
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    <WorldCardActions
                      worldId={world.id}
                      worldName={world.name}
                      role={world.permission}
                      allowCloning={world.allow_cloning}
                      onView={() => navigate(`/world/${world.id}`)}
                      onEdit={world.permission === "edit" ? () => navigate(`/world/${world.id}`) : undefined}
                      onPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
                      onDuplicate={world.permission === "edit" ? () => handleCloneWorld(world.id, world.name) : undefined}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
